import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { StaffRole } from '@/api/types.js';

import {
  adoptTokens,
  completeLogin,
  getStoredUser,
  type RawTokens,
  startLogin,
  startLogout,
  type User as OidcUser,
  userManager,
} from '@/auth/oidc.js';

/** Роль сотрудника в конкретном мероприятии. */
export interface EventStaffRole {
  eventId: string;
  role: StaffRole;
  /** Роли участия, которые ведёт сотрудник. Пусто — все. */
  scopeRoleIds: string[];
  /** Мероприятия события, людей которых ведёт сотрудник. Пусто — все. */
  scopeSubeventIds?: string[];
  /** Утверждает заявки сам, а не только предлагает решение владельцу. */
  canDecide: boolean;
}

/**
 * Текущий пользователь events-web.
 *
 * Идентичность берём из JWT, а права — НЕ из токена: их отдаёт наш бэк по
 * `GET /api/v1/me` на основании локальных таблиц (`platform_admins`,
 * `event_staff`). Клиентские роли Keycloak ничего не знают про конкретные
 * мероприятия, поэтому источник истины только один — БД сервиса.
 */
export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  /** Имя и фамилия из профиля Keycloak — для предзаполнения анкеты. */
  firstName: string;
  lastName: string;
  isPlatformAdmin: boolean;
  staff: EventStaffRole[];
  /** Мероприятия, где человек — делегат компании-участницы. */
  registrarOf: EventRegistrarLink[];
}

/** Право делегата в мероприятии. */
export interface EventRegistrarLink {
  eventId: string;
  subeventId: string | null;
}

interface MeResponse {
  id: string;
  email?: string;
  displayName?: string;
  isPlatformAdmin: boolean;
  staff: EventStaffRole[];
  registrarOf?: EventRegistrarLink[];
  isServiceAccount: boolean;
}

const apiBase = import.meta.env.VITE_API_URL.replace(/\/$/, '');

async function fetchMe(token: string): Promise<MeResponse | null> {
  try {
    const r = await fetch(`${apiBase}/api/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return (await r.json()) as MeResponse;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<CurrentUser | null>(null);
  const isLoading = ref(true);

  const isAuthenticated = computed(() => Boolean(user.value) && Boolean(accessToken.value));
  const isPlatformAdmin = computed(() => user.value?.isPlatformAdmin ?? false);
  const staff = computed<EventStaffRole[]>(() => user.value?.staff ?? []);
  const registrarOf = computed<EventRegistrarLink[]>(() => user.value?.registrarOf ?? []);
  /**
   * Есть ли доступ во внутреннюю часть: админ платформы, оргкоманда или
   * делегат компании-участницы.
   *
   * Делегата тут не хватало, и получалось нелепо: backend открыт, права в
   * базе есть, а на входе «вы не состоите ни в одной оргкоманде» — он в ней
   * и не должен состоять.
   */
  const hasAccess = computed(
    () => isPlatformAdmin.value || staff.value.length > 0 || registrarOf.value.length > 0,
  );

  /** Делегат ли пользователь в конкретном мероприятии. */
  function isRegistrarIn(eventId: string): boolean {
    return registrarOf.value.some((r) => r.eventId === eventId);
  }

  /** Роли пользователя в конкретном мероприятии. */
  function rolesIn(eventId: string): EventStaffRole['role'][] {
    return staff.value.filter((s) => s.eventId === eventId).map((s) => s.role);
  }

  /**
   * Роли участия, которыми ограничен пользователь. Пусто — ограничения нет.
   *
   * Повторяет правило бэкенда (`staffRoleScope`): владелец не ограничивается,
   * и полные права хотя бы в одной записи снимают ограничение целиком.
   * Расходиться им нельзя — иначе экран прячет то, что сервер отдаёт.
   */
  function participantRoleScopeIn(eventId: string): string[] {
    return streamIn(eventId).roleIds;
  }

  /**
   * Поток пользователя: роли и мероприятия. Пусто в обоих — без ограничения.
   *
   * Повторяет `staffStreamScope` бэкенда, включая то, что у согласующего
   * пустой поток значит «ничего», а не «всё».
   */
  function streamIn(eventId: string): { roleIds: string[]; subeventIds: string[] } {
    const all = { roleIds: [], subeventIds: [] };
    if (isPlatformAdmin.value) return all;
    const rows = staff.value.filter((s) => s.eventId === eventId);
    if (rows.length === 0) return all;
    const hasStream = (s: EventStaffRole) =>
      s.scopeRoleIds.length > 0 || (s.scopeSubeventIds ?? []).length > 0;
    if (rows.some((s) => s.role === 'owner' || (s.role !== 'reviewer' && !hasStream(s))))
      return all;
    const streams = rows.filter(hasStream);
    return {
      roleIds: [...new Set(streams.flatMap((s) => s.scopeRoleIds))],
      subeventIds: [...new Set(streams.flatMap((s) => s.scopeSubeventIds ?? []))],
    };
  }

  /**
   * Только ли согласующий пользователь в этом мероприятии.
   *
   * Согласующий — внешний человек: ему показывается одно согласование, без
   * заведения людей, ссылок, замены и правки карточки.
   */
  function isReviewerOnlyIn(eventId: string): boolean {
    if (isPlatformAdmin.value) return false;
    const roles = rolesIn(eventId);
    return roles.length > 0 && roles.every((r) => r === 'reviewer');
  }

  /**
   * Вправе ли пользователь принимать решение по заявке, а не только предлагать.
   *
   * По умолчанию не вправе никто, кроме владельца: право выдаётся поимённо.
   */
  function canDecideIn(eventId: string): boolean {
    if (isPlatformAdmin.value) return true;
    return staff.value.some((s) => s.eventId === eventId && (s.role === 'owner' || s.canDecide));
  }

  async function hydrate(oidcUser: OidcUser | null): Promise<void> {
    if (!oidcUser || oidcUser.expired) {
      accessToken.value = null;
      user.value = null;
      return;
    }
    accessToken.value = oidcUser.access_token;

    const me = await fetchMe(oidcUser.access_token);
    const profile = oidcUser.profile as Record<string, unknown>;
    const firstName = (profile.given_name as string | undefined) ?? '';
    const lastName = (profile.family_name as string | undefined) ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const profileEmail = (profile.email as string | undefined) ?? '';

    // Первое непустое значение: пустая строка означает «не заполнено», поэтому
    // именно фильтр, а не ?? — тот пропустил бы '' дальше как валидное имя.
    const displayName =
      [
        me?.displayName,
        fullName,
        profile.preferred_username as string | undefined,
        profileEmail,
      ].find((v) => typeof v === 'string' && v.trim() !== '') ?? '';

    user.value = {
      id: oidcUser.profile.sub,
      email: me?.email ?? profileEmail,
      displayName,
      firstName,
      lastName,
      isPlatformAdmin: me?.isPlatformAdmin ?? false,
      staff: me?.staff ?? [],
      registrarOf: me?.registrarOf ?? [],
    };
  }

  async function initialize(): Promise<void> {
    isLoading.value = true;
    try {
      await hydrate(await getStoredUser());

      userManager.events.addUserLoaded((u) => {
        void hydrate(u);
      });
      userManager.events.addUserUnloaded(() => {
        void hydrate(null);
      });
      userManager.events.addAccessTokenExpired(() => {
        void hydrate(null);
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function login(returnTo?: string, loginHint?: string): Promise<void> {
    await startLogin(returnTo, loginHint);
  }

  /**
   * Войти по токенам, полученным от бэкенда.
   *
   * Используется после самостоятельной регистрации по ссылке: учётка создана,
   * пароль человек только что придумал, и второй раз спрашивать его нечестно.
   *
   * @returns удалось ли войти. `false` — нужен обычный вход через Keycloak.
   */
  async function adopt(tokens: RawTokens): Promise<boolean> {
    const oidcUser = await adoptTokens(tokens);
    if (!oidcUser) return false;
    await hydrate(oidcUser);
    return isAuthenticated.value;
  }

  async function handleCallback(): Promise<string> {
    const { user: oidcUser, returnTo } = await completeLogin();
    await hydrate(oidcUser);
    return returnTo;
  }

  async function logout(): Promise<void> {
    await startLogout();
  }

  /** Перечитать `/me` — например, после выдачи роли в мероприятии. */
  async function refresh(): Promise<void> {
    if (!accessToken.value) return;
    const me = await fetchMe(accessToken.value);
    if (me && user.value) {
      user.value = {
        ...user.value,
        email: me.email ?? user.value.email,
        displayName: me.displayName ?? user.value.displayName,
        isPlatformAdmin: me.isPlatformAdmin,
        staff: me.staff,
        // Без этого только что принятое приглашение регистратора не
        // замечалось до перезахода: права на сервере уже были, а интерфейс
        // отправлял человека на «Нет доступа».
        registrarOf: me.registrarOf ?? user.value.registrarOf,
      };
    }
  }

  return {
    accessToken,
    user,
    isLoading,
    isAuthenticated,
    isPlatformAdmin,
    staff,
    registrarOf,
    hasAccess,
    rolesIn,
    participantRoleScopeIn,
    streamIn,
    isReviewerOnlyIn,
    canDecideIn,
    isRegistrarIn,
    initialize,
    login,
    adopt,
    handleCallback,
    logout,
    refresh,
  };
});
